import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ShieldCheck,
  Leaf,
  Radar,
  FileCheck,
  Zap,
  TruckIcon,
  PackageCheck,
  Timer,
  MapPin,
  Layers,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { useLang } from '../context/LanguageContext';
import { STATS, IMAGES } from '../mock';

const SectionEyebrow = ({ children }) => (
  <div className="inline-flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-[0.18em] text-blue-800">
    <span className="w-6 h-px bg-red-500" />
    {children}
  </div>
);

const Home = () => {
  const { t, lang } = useLang();

  return (
    <div>
      <SEO page="home" />
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 to-white">
        <div className="absolute inset-0 pointer-events-none opacity-[0.35]">
          <div className="absolute -top-32 -right-32 w-[540px] h-[540px] rounded-full bg-blue-100 blur-3xl" />
          <div className="absolute top-40 -left-40 w-[520px] h-[520px] rounded-full bg-sky-100 blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-20 md:pb-28 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-6">
            <SectionEyebrow>{t.hero.eyebrow}</SectionEyebrow>
            <h1 className="mt-5 text-[44px] sm:text-[54px] lg:text-[64px] leading-[1.02] font-semibold tracking-tight text-slate-900">
              {t.hero.title}
              <br />
              <span className="text-slate-500">{t.hero.titleAccent}</span>
            </h1>
            <p className="mt-6 text-[16.5px] md:text-[17px] leading-relaxed text-slate-600 max-w-xl">
              {t.hero.subtitle}
            </p>

            <ul className="mt-8 grid sm:grid-cols-3 gap-3">
              {[t.hero.pointA, t.hero.pointB, t.hero.pointC].map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-[13.5px] font-medium text-slate-700">
                  <CheckCircle2 size={17} className="mt-0.5 text-blue-700 shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link to="/contact">
                <Button className="bg-blue-700 hover:bg-blue-800 text-white rounded-full px-6 h-12 text-[15px] font-medium shadow-[0_10px_28px_-12px_rgba(29,78,216,0.55)]">
                  {t.cta.requestQuote}
                  <ArrowRight size={17} className="ml-1.5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="ghost" className="rounded-full px-6 h-12 text-[15px] font-medium text-slate-800 hover:bg-slate-100">
                  {t.cta.learnMore}
                </Button>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative rounded-[24px] overflow-hidden shadow-[0_40px_80px_-30px_rgba(11,29,79,0.4)] ring-1 ring-slate-900/5 bg-gradient-to-br from-[#0B1D4F] via-[#0F2A6E] to-[#0B1D4F]">
              {/* subtle radial glow */}
              <div className="absolute inset-0 opacity-60 pointer-events-none">
                <div className="absolute -top-20 -right-20 w-[420px] h-[420px] rounded-full bg-blue-400/20 blur-3xl" />
                <div className="absolute -bottom-24 -left-16 w-[380px] h-[380px] rounded-full bg-blue-500/10 blur-3xl" />
              </div>
              {/* thin red artery */}
              <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-red-500/70 to-transparent" />
              <div className="absolute bottom-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-red-500/50 to-transparent" />

              <img
                src={IMAGES.heroTruck}
                alt="VenaTerra — Blood of Logistics"
                className="relative w-full h-[460px] md:h-[560px] object-contain p-4 md:p-6"
                loading="eager"
              />
            </div>

            {/* Stats card */}
            <div className="hidden md:block absolute -bottom-8 -right-4 bg-white rounded-2xl shadow-2xl ring-1 ring-slate-900/5 px-5 py-4 w-[240px]">
              <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{lang === 'cs' ? 'Odezva na poptávku' : 'Quote response'}</div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-[32px] font-semibold text-slate-900 leading-none">30</span>
                <span className="text-[14px] text-slate-600 font-medium">min</span>
              </div>
              <div className="mt-1 text-[12.5px] text-slate-500">{lang === 'cs' ? 'v pracovní době' : 'during business hours'}</div>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 pb-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 rounded-2xl overflow-hidden ring-1 ring-slate-200">
            {STATS.map((s) => (
              <div key={s.key} className="bg-white px-5 py-6 flex flex-col">
                <div className="text-[28px] md:text-[32px] font-semibold text-slate-900 tracking-tight leading-none">
                  {lang === 'cs' ? s.valueCs : s.valueEn}
                </div>
                <div className="mt-2 text-[13px] text-slate-500 font-medium">
                  {lang === 'cs' ? s.labelCs : s.labelEn}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST / PROCUREMENT */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-3xl">
            <SectionEyebrow>{lang === 'cs' ? 'Pro nákupčí' : 'For procurement'}</SectionEyebrow>
            <h2 className="mt-4 text-[34px] md:text-[44px] leading-[1.08] font-semibold tracking-tight text-slate-900">
              {t.trust.title}
            </h2>
            <p className="mt-4 text-[16px] text-slate-600 max-w-2xl leading-relaxed">
              {t.trust.subtitle}
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: ShieldCheck, ...t.trust.insurance, color: 'orange' },
              { icon: Leaf, ...t.trust.fleet, color: 'emerald' },
              { icon: Radar, ...t.trust.gps, color: 'sky' },
              { icon: FileCheck, ...t.trust.legal, color: 'slate' },
            ].map((item, i) => {
              const Icon = item.icon;
              const colorMap = {
                orange: 'bg-red-50 text-blue-800',
                emerald: 'bg-emerald-50 text-emerald-600',
                sky: 'bg-sky-50 text-sky-600',
                slate: 'bg-slate-100 text-slate-700',
              };
              return (
                <div key={i} className="group relative bg-white rounded-2xl border border-slate-200 p-6 hover:border-slate-300 hover:shadow-[0_20px_40px_-24px_rgba(15,23,42,0.15)] transition-all duration-300">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${colorMap[item.color]}`}>
                    <Icon size={20} />
                  </div>
                  <div className="mt-5 text-[16.5px] font-semibold text-slate-900">{item.title}</div>
                  <div className="mt-2 text-[14px] text-slate-600 leading-relaxed">{item.desc}</div>
                  <div className="absolute inset-x-6 bottom-0 h-px bg-gradient-to-r from-transparent via-blue-700/50 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500" />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <SectionEyebrow>{t.services.eyebrow}</SectionEyebrow>
              <h2 className="mt-4 text-[34px] md:text-[44px] leading-[1.08] font-semibold tracking-tight text-slate-900">
                {t.services.title}
              </h2>
              <p className="mt-4 text-[16px] text-slate-600 leading-relaxed">
                {t.services.subtitle}
              </p>
            </div>
            <Link to="/services" className="hidden md:inline-flex items-center gap-1.5 text-[14px] font-medium text-slate-900 hover:text-blue-800 transition-colors">
              {lang === 'cs' ? 'Všechny služby' : 'All services'}
              <ArrowRight size={15} />
            </Link>
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-5">
            {t.services.items.map((s, i) => {
              const icons = [Zap, TruckIcon, Layers, PackageCheck, Timer];
              const Icon = icons[i] || Zap;
              return (
                <div key={s.id} className="group relative bg-white rounded-2xl border border-slate-200 p-7 hover:border-blue-200 hover:-translate-y-0.5 transition-all duration-300">
                  <div className="flex items-start justify-between gap-4">
                    <div className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center group-hover:bg-blue-700 transition-colors">
                      <Icon size={22} />
                    </div>
                    <div className="text-[13px] font-mono text-slate-400">0{i + 1}</div>
                  </div>
                  <h3 className="mt-6 text-[20px] font-semibold text-slate-900 tracking-tight">{s.title}</h3>
                  <p className="mt-2 text-[14.5px] text-slate-600 leading-relaxed">{s.desc}</p>
                  <ul className="mt-5 space-y-1.5">
                    {s.bullets.map((b, j) => (
                      <li key={j} className="flex items-center gap-2 text-[13.5px] text-slate-700">
                        <span className="w-1 h-1 rounded-full bg-blue-700" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FLEET PREVIEW */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 relative">
            <div className="rounded-3xl overflow-hidden ring-1 ring-slate-900/5 shadow-[0_40px_80px_-30px_rgba(11,29,79,0.35)] bg-gradient-to-br from-[#0B1D4F] to-[#0F2A6E]">
              <img
                src={IMAGES.fleetBanner}
                alt="VenaTerra fleet — black tarp van, sprinter and semi truck"
                className="w-full h-auto object-contain"
                loading="lazy"
              />
            </div>
            <div className="absolute -bottom-6 -right-4 md:right-6 bg-white rounded-2xl px-5 py-4 shadow-xl ring-1 ring-slate-900/5 flex items-center gap-3.5">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <Leaf size={20} />
              </div>
              <div>
                <div className="text-[13px] font-semibold text-slate-900">100% Euro 6</div>
                <div className="text-[12px] text-slate-500">{lang === 'cs' ? 'ESG-ready park' : 'ESG-ready fleet'}</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6">
            <SectionEyebrow>{t.fleet.eyebrow}</SectionEyebrow>
            <h2 className="mt-4 text-[34px] md:text-[42px] leading-[1.08] font-semibold tracking-tight text-slate-900">
              {t.fleet.title}
            </h2>
            <p className="mt-4 text-[16px] text-slate-600 leading-relaxed">
              {t.fleet.subtitle}
            </p>

            <dl className="mt-8 divide-y divide-slate-200 border-y border-slate-200">
              {t.fleet.specs.slice(0, 4).map((spec, i) => (
                <div key={i} className="py-3.5 flex items-baseline justify-between gap-6">
                  <dt className="text-[13.5px] text-slate-500 font-medium">{spec.label}</dt>
                  <dd className="text-[14.5px] text-slate-900 font-semibold text-right">{spec.value}</dd>
                </div>
              ))}
            </dl>

            <Link to="/fleet" className="mt-7 inline-flex items-center gap-1.5 text-[14.5px] font-medium text-blue-800 hover:text-red-800 transition-colors">
              {lang === 'cs' ? 'Kompletní parametry vozového parku' : 'Full fleet specifications'}
              <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="py-16 md:py-20 bg-[#0B1D4F] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute -top-20 -right-10 w-[500px] h-[500px] rounded-full bg-blue-700 blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-[0.18em] text-blue-400">
              <MapPin size={13} />
              {lang === 'cs' ? 'Připraveni odbavit vaši zásilku?' : 'Ready to move your cargo?'}
            </div>
            <h3 className="mt-3 text-[28px] md:text-[36px] leading-tight font-semibold tracking-tight">
              {lang === 'cs' ? 'Získejte nezávaznou nabídku do 30 minut.' : 'Get a non-binding quote within 30 minutes.'}
            </h3>
            <p className="mt-3 text-slate-400 text-[15px] max-w-xl">
              {lang === 'cs' ? 'Popište nám trasu, druh a hmotnost zboží — dispečink odpoví obratem v pracovní době.' : 'Tell us the route, cargo type and weight — dispatch responds immediately during business hours.'}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact">
              <Button className="bg-blue-700 hover:bg-blue-800 text-white rounded-full px-6 h-12 text-[15px] font-medium">
                {t.cta.requestQuote}
                <ArrowRight size={17} className="ml-1.5" />
              </Button>
            </Link>
            <Link to="/about">
              <Button variant="ghost" className="rounded-full px-6 h-12 text-[15px] font-medium text-white hover:bg-white/10">
                {t.nav.about}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
